// frontend/src/pages/Admin/APIKeys.tsx
// Page admin : gestion des clés API SmartLawyer + logs de recherche + logs agent

import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ApiKey {
  id:           string;
  key:          string;
  key_masked:   string;
  email:        string;
  user_id:      string;
  plan:         string;
  active:       boolean;
  created_at:   string;
  last_used:    string;
  search_count: number;
  calls_30d:    number;
}

interface UsageSeries {
  date: string;
  total: number;
  [source: string]: number | string;
}

interface Usage {
  days: number;
  since: string;
  bucket: 'day' | 'week';
  total: number;
  by_source: Record<string, number>;
  series: UsageSeries[];
  signups: {
    total: number;
    firestore_only: number;
    legacy_paid: number;
    in_period: number;
    with_api_key: number;
    paid: number;
    first: string | null;
    series: number[];
  };
  api: {
    calls: number;
    unique_keys: number;
    unique_users: number;
    unique_ips: number;
    avg_ms: number | null;
    p95_ms: number | null;
    no_result_rate: number | null;
    top_queries: { query: string; count: number }[];
    top_keys: { key_masked: string; name: string; calls: number }[];
  };
}

const SERIES_SOURCES = [
  { key: 'fiche',  label: 'Fiches',    color: 'bg-blue-500' },
  { key: 'mcp',    label: 'MCP',       color: 'bg-purple-500' },
  { key: 'public', label: 'Recherche', color: 'bg-emerald-500' },
  { key: 'agent',  label: 'Agent IA',  color: 'bg-amber-500' },
] as const;

const PERIODS = [
  { days: 1, label: '24 h' },
  { days: 7, label: '7 jours' },
  { days: 30, label: '30 jours' },
  { days: 90, label: '90 jours' },
  { days: 3650, label: 'Tout' },
];

const nf = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('fr-FR'));

interface SearchLog {
  id:               string;
  source:           string;
  query:            string;
  filters:          Record<string, string>;
  results_count:    number;
  response_time_ms: number;
  timestamp:        string;
  key_id:           string;
  key_name?:        string;
  uid:              string;
  email:            string;
  ip:               string;
  user_agent:       string;
  // fiche
  ecli?:            string;
  number?:          string;
  chamber?:         string;
  jurisdiction?:    string;
  decision_date?:   string;
  referrer?:        string;
  // agent
  fiche_id?:        string;
  fiche_context?:   string;
  agent_name?:      string;
  session_id?:      string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
};

const fmtMs = (ms: number) => ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;

const PlanBadge = ({ plan }: { plan: string }) => {
  const styles: Record<string, string> = {
    free:    'bg-muted text-muted-foreground',
    pro:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    premium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[plan] || styles.free}`}>
      {plan}
    </span>
  );
};

const SourceBadge = ({ source }: { source: string }) => {
  const cfg: Record<string, { label: string; className: string }> = {
    mcp:          { label: '⚡ MCP',         className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
    public:       { label: '🔍 Recherche',   className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
    fiche:        { label: '📄 Fiche',       className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    agent:        { label: '🧭 Agent IA',    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    fiche_agent:  { label: '🤖 Chat fiche',  className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  };
  const s = cfg[source] ?? { label: source, className: 'bg-muted text-muted-foreground' };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.className}`}>
      {s.label}
    </span>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

const APIKeysContent = () => {
  const { user, isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate  = useNavigate();

  const [keys, setKeys]               = useState<ApiKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [keysError, setKeysError]     = useState('');
  const [search, setSearch]           = useState('');

  const [selectedKey, setSelectedKey]         = useState<ApiKey | null>(null);
  const [logs, setLogs]                       = useState<SearchLog[]>([]);
  const [allLogs, setAllLogs]                 = useState<SearchLog[]>([]);
  const [loadingLogs, setLoadingLogs]         = useState(false);
  const [activeTab, setActiveTab]             = useState<'keys' | 'logs'>('keys');
  const [logSource, setLogSource]             = useState<'all' | 'public' | 'fiche' | 'agent' | 'mcp' | 'fiche_agent'>('all');
  const [logDays, setLogDays]                 = useState(7);
  const [loadingAllLogs, setLoadingAllLogs]   = useState(false);
  const [allLogsTotal, setAllLogsTotal]       = useState(0);
  const [allNextBefore, setAllNextBefore]     = useState<string | null>(null);
  const [keyLogsTotal, setKeyLogsTotal]       = useState(0);
  const [keyNextBefore, setKeyNextBefore]     = useState<string | null>(null);
  const [loadingMore, setLoadingMore]         = useState(false);
  const [usageDays, setUsageDays]             = useState(30);
  const [usage, setUsage]                     = useState<Usage | null>(null);
  const [usageError, setUsageError]           = useState('');
  const [chartWithFiches, setChartWithFiches] = useState(false);
  const [usageReload, setUsageReload]         = useState(0);
  const [syncing, setSyncing]                 = useState(false);
  const [syncResult, setSyncResult]           = useState('');

  const syncFirebase = async () => {
    setSyncing(true);
    setSyncResult('');
    try {
      const r = await fetch(apiClient.buildEndpoint('/api/users/public-users/sync'), { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setSyncResult(`Firebase : ${nf(d.firebase_users)} comptes + ${nf(d.firestore_only)} Firestore (ancienne app) — ${nf(d.added)} ajouté(s), ${nf(d.updated)} mis à jour`);
      setUsageReload(n => n + 1);
    } catch (e) {
      setSyncResult(`Synchronisation impossible : ${(e as Error).message}`);
    } finally {
      setSyncing(false);
    }
  };

  const get = (path: string) =>
    fetch(apiClient.buildEndpoint(path), { credentials: 'include' }).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    });

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated, isReady, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !isReady) return;
    setLoadingKeys(true);
    get('/api/fiches/admin/api-keys')
      .then(d => { setKeys(d.keys || []); setLoadingKeys(false); })
      .catch(e => { setKeysError(`Erreur chargement clés : ${e.message}`); setLoadingKeys(false); });
  }, [isAuthenticated, isReady]);

  useEffect(() => {
    if (!isAuthenticated || !isReady) return;
    setUsage(null);
    setUsageError('');
    get(`/api/fiches/admin/usage?days=${usageDays}`)
      .then(setUsage)
      .catch(e => setUsageError(e.message));
  }, [isAuthenticated, isReady, usageDays, usageReload]);

  const logsPath = (before?: string | null) => {
    const params = new URLSearchParams({ limit: '100', days: String(logDays) });
    if (logSource !== 'all') params.set('source', logSource);
    if (before) params.set('before', before);
    return `/api/fiches/admin/logs?${params}`;
  };

  useEffect(() => {
    if (activeTab !== 'logs') return;
    setLoadingAllLogs(true);
    get(logsPath())
      .then(d => { setAllLogs(d.logs || []); setAllLogsTotal(d.total || 0); setAllNextBefore(d.next_before); })
      .catch(() => { setAllLogs([]); setAllLogsTotal(0); setAllNextBefore(null); })
      .finally(() => setLoadingAllLogs(false));
  }, [activeTab, logSource, logDays]);

  const keyLogsPath = (key: ApiKey, before?: string | null) =>
    `/api/fiches/admin/api-keys/${encodeURIComponent(key.key)}/logs?limit=100${before ? `&before=${encodeURIComponent(before)}` : ''}`;

  const loadKeyLogs = async (key: ApiKey) => {
    setSelectedKey(key);
    setLoadingLogs(true);
    setLogs([]);
    try {
      const d = await get(keyLogsPath(key));
      setLogs(d.logs || []);
      setKeyLogsTotal(d.total || 0);
      setKeyNextBefore(d.next_before);
    } catch { /* noop */ }
    finally { setLoadingLogs(false); }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      if (selectedKey) {
        const d = await get(keyLogsPath(selectedKey, keyNextBefore));
        setLogs(prev => [...prev, ...(d.logs || [])]);
        setKeyNextBefore(d.next_before);
      } else {
        const d = await get(logsPath(allNextBefore));
        setAllLogs(prev => [...prev, ...(d.logs || [])]);
        setAllNextBefore(d.next_before);
      }
    } catch { /* noop */ }
    finally { setLoadingMore(false); }
  };

  if (!isReady) return (
    <div className="flex items-center justify-center h-full"><Loader className="!size-6" /></div>
  );
  if (!isAuthenticated || !user) return null;

  const filteredKeys = keys.filter(k =>
    !search ||
    k.email.toLowerCase().includes(search.toLowerCase()) ||
    k.key_masked.toLowerCase().includes(search.toLowerCase()) ||
    k.plan.toLowerCase().includes(search.toLowerCase())
  );

  const displayLogs = selectedKey ? logs : allLogs;
  const displayTotal = selectedKey ? keyLogsTotal : allLogsTotal;
  const hasMore = selectedKey ? !!keyNextBefore : !!allNextBefore;
  const chartSources = SERIES_SOURCES.filter(s => chartWithFiches || s.key !== 'fiche');
  const bucketTotal = (b: UsageSeries) => chartSources.reduce((n, s) => n + ((b[s.key] as number) || 0), 0);
  const maxBucket = Math.max(1, ...(usage?.series || []).map(bucketTotal));

  return (
    <div className="flex flex-col h-full w-full overflow-auto">
      <div className="max-w-6xl mx-auto w-full p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate('/admin')}
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Retour
            </button>
            <h1 className="text-2xl font-bold">📋 Clés API & Logs</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Monitoring des recherches et questions agent SmartLawyer
            </p>
          </div>
        </div>

        {/* Usage */}
        <section aria-labelledby="usage-title" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 id="usage-title" className="text-lg font-semibold">Usage</h2>
              <button onClick={syncFirebase} disabled={syncing}
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border hover:bg-accent disabled:opacity-60">
                {syncing && <Loader className="!size-3.5" />}
                Synchroniser les inscrits Firebase
              </button>
              {syncResult && <span role="status" className="text-xs text-muted-foreground">{syncResult}</span>}
            </div>
            <div className="flex rounded-lg border overflow-hidden" role="group" aria-label="Période">
              {PERIODS.map(p => (
                <button key={p.days} onClick={() => setUsageDays(p.days)} aria-pressed={usageDays === p.days}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    usageDays === p.days ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-accent'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {usageError && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              Statistiques indisponibles : {usageError}
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Appels au total',        value: nf(usage?.total),                    hint: 'toutes sources' },
              { label: 'Consultations de fiches', value: nf(usage?.by_source.fiche),         hint: 'pages fiche vues' },
              { label: 'Appels API & MCP',       value: nf(usage?.api.calls),                hint: `${nf(usage?.by_source.mcp)} MCP · ${nf(usage?.by_source.public)} recherche · ${nf(usage?.by_source.agent)} agent` },
              { label: 'Clés API actives',       value: nf(usage?.api.unique_keys),          hint: `sur ${nf(keys.length)} clés` },
              { label: 'Utilisateurs API',       value: nf(usage?.api.unique_users),         hint: `${nf(usage?.api.unique_ips)} IP distinctes` },
              { label: 'Temps de réponse',       value: usage?.api.avg_ms != null ? fmtMs(usage.api.avg_ms) : '—', hint: usage?.api.p95_ms != null ? `p95 ${fmtMs(usage.api.p95_ms)}` : '' },
              { label: 'Sans résultat',          value: usage?.api.no_result_rate != null ? `${(usage.api.no_result_rate * 100).toFixed(1)} %` : '—', hint: 'recherches API' },
              { label: 'Clés API au total',      value: nf(keys.length),                     hint: `${nf(keys.filter(k => k.calls_30d > 0).length)} utilisées sur 30 j` },
              { label: 'Inscrits',               value: nf(usage?.signups.total),            hint: [usage?.signups.first ? `depuis le ${new Date(usage.signups.first).toLocaleDateString('fr-FR')}` : '', usage?.signups.firestore_only ? `dont ${nf(usage.signups.firestore_only)} ancienne app` : ''].filter(Boolean).join(' · ') },
              { label: 'Nouveaux inscrits',      value: nf(usage?.signups.in_period),        hint: 'sur la période' },
              { label: 'Inscrits avec clé API',  value: nf(usage?.signups.with_api_key),     hint: usage?.signups.total ? `${Math.round((usage.signups.with_api_key / usage.signups.total) * 100)} % des inscrits` : '' },
              { label: 'Abonnés',                value: nf(usage?.signups.paid),             hint: usage?.signups.legacy_paid ? `plan payant · + ${nf(usage.signups.legacy_paid)} ayant payé sur l'ancienne app` : 'plan payant' },
            ].map(({ label, value, hint }) => (
              <div key={label} className="rounded-xl border bg-card p-4">
                <p className="text-xs text-muted-foreground">{label}</p>
                {usage || label === 'Clés API au total'
                  ? <p className="text-2xl font-bold tabular-nums mt-1">{value}</p>
                  : <div className="h-8 w-24 mt-1 animate-pulse rounded bg-muted" />}
                {hint && <p className="text-[11px] text-muted-foreground mt-1 truncate">{hint}</p>}
              </div>
            ))}
          </div>

          {usage && usage.series.length > 0 && (
            <div className="rounded-xl border bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-sm font-medium">Appels par {usage.bucket === 'day' ? 'jour' : 'semaine'}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={chartWithFiches} onChange={e => setChartWithFiches(e.target.checked)} />
                    Inclure les consultations de fiches
                  </label>
                  {chartSources.map(s => (
                    <span key={s.key} className="inline-flex items-center gap-1.5">
                      <span className={`h-2.5 w-2.5 rounded-sm ${s.color}`} aria-hidden="true" />{s.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-end gap-[2px] h-40" role="img"
                aria-label={`Appels par ${usage.bucket === 'day' ? 'jour' : 'semaine'}, ${usage.series.length} périodes`}>
                {usage.series.map(b => {
                  const total = bucketTotal(b);
                  return (
                    <div key={b.date} className="flex-1 min-w-[3px] flex flex-col-reverse rounded-sm overflow-hidden hover:opacity-80"
                      style={{ height: `${Math.max(2, (total / maxBucket) * 100)}%` }}
                      title={`${new Date(b.date).toLocaleDateString('fr-FR')} — ${nf(total)} appels (${chartSources.map(s => `${s.label} ${nf(b[s.key] as number)}`).join(', ')})`}>
                      {chartSources.map(s => (
                        <div key={s.key} className={s.color} style={{ height: `${total ? ((b[s.key] as number) / total) * 100 : 0}%` }} />
                      ))}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>{new Date(usage.series[0].date).toLocaleDateString('fr-FR')}</span>
                <span>{new Date(usage.series[usage.series.length - 1].date).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
          )}

          {usage && usage.signups.series.some(n => n > 0) && (
            <div className="rounded-xl border bg-card p-4">
              <p className="text-sm font-medium mb-3">Inscriptions par {usage.bucket === 'day' ? 'jour' : 'semaine'}</p>
              <div className="flex items-end gap-[2px] h-20" role="img" aria-label="Inscriptions par période">
                {usage.signups.series.map((n, i) => {
                  const max = Math.max(1, ...usage.signups.series);
                  return (
                    <div key={usage.series[i]?.date ?? i} className="flex-1 min-w-[3px] rounded-sm bg-violet-500 hover:opacity-80"
                      style={{ height: `${Math.max(n ? 4 : 1, (n / max) * 100)}%`, opacity: n ? 1 : 0.25 }}
                      title={`${usage.series[i] ? new Date(usage.series[i].date).toLocaleDateString('fr-FR') : ''} — ${nf(n)} inscription(s)`} />
                  );
                })}
              </div>
            </div>
          )}

          {usage && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border bg-card p-4">
                <p className="text-sm font-medium mb-2">Requêtes les plus fréquentes (API & MCP)</p>
                {usage.api.top_queries.length === 0 ? <p className="text-sm text-muted-foreground">Aucune</p> : (
                  <ol className="space-y-1 text-sm">
                    {usage.api.top_queries.map(q => (
                      <li key={q.query} className="flex items-center justify-between gap-3">
                        <span className="truncate">{q.query}</span>
                        <span className="tabular-nums text-muted-foreground">{nf(q.count)}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
              <div className="rounded-xl border bg-card p-4">
                <p className="text-sm font-medium mb-2">Clés les plus actives sur la période</p>
                {usage.api.top_keys.length === 0 ? <p className="text-sm text-muted-foreground">Aucune</p> : (
                  <ol className="space-y-1 text-sm">
                    {usage.api.top_keys.map(k => {
                      const owner = keys.find(x => x.key_masked === k.key_masked);
                      return (
                        <li key={k.key_masked} className="flex items-center justify-between gap-3">
                          <span className="truncate">{owner?.email || k.name || <span className="font-mono text-xs">{k.key_masked}</span>}</span>
                          <span className="tabular-nums text-muted-foreground">{nf(k.calls)}</span>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {(['keys', 'logs'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 text-sm font-medium rounded-t-lg -mb-px border transition-colors ${
                activeTab === tab
                  ? 'bg-background border-border border-b-background text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              {tab === 'keys' ? '🔑 Clés API' : '📋 Logs globaux'}
            </button>
          ))}
        </div>

        {/* ── TAB CLÉS ── */}
        {activeTab === 'keys' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher par email, clé..."
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <span className="text-sm text-muted-foreground">{filteredKeys.length} clé(s)</span>
            </div>

            {keysError && (
              <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{keysError}</div>
            )}

            {loadingKeys ? (
              <div className="flex justify-center py-12"><Loader className="!size-6" /></div>
            ) : (
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        {['Email', 'Clé', 'Plan', 'Statut', 'Appels', '30 j', 'Dernière utilisation', 'Créée le', 'Actions'].map(h => (
                          <th key={h} className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 py-3">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredKeys.length === 0 ? (
                        <tr><td colSpan={9} className="text-center py-12 text-muted-foreground">Aucune clé API</td></tr>
                      ) : filteredKeys.map(key => (
                        <tr key={key.id} className="border-b hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3">
                            <p className="font-medium truncate max-w-[180px]">{key.email || '—'}</p>
                            {key.user_id && <p className="text-xs text-muted-foreground font-mono truncate max-w-[180px]">{key.user_id}</p>}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{key.key_masked}</td>
                          <td className="px-4 py-3"><PlanBadge plan={key.plan} /></td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                              key.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${key.active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                              {key.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center font-semibold tabular-nums">{nf(key.search_count || 0)}</td>
                          <td className="px-4 py-3 text-center tabular-nums text-muted-foreground">{nf(key.calls_30d || 0)}</td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(key.last_used)}</td>
                          <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(key.created_at)}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => { setActiveTab('logs'); loadKeyLogs(key); }}
                              className="text-xs px-3 py-1.5 rounded-lg border hover:bg-accent hover:border-primary/30 transition-colors text-muted-foreground hover:text-foreground">
                              Voir logs →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB LOGS ── */}
        {activeTab === 'logs' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Source filter */}
              <div className="flex rounded-lg border overflow-hidden flex-wrap">
                {([
                  { key: 'all',         label: 'Tous' },
                  { key: 'public',      label: '🔍 Recherche' },
                  { key: 'fiche',       label: '📄 Fiches' },
                  { key: 'agent',       label: '🧭 Agent IA' },
                  { key: 'mcp',         label: '⚡ MCP' },
                ] as const).map(s => (
                  <button key={s.key} onClick={() => setLogSource(s.key)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      logSource === s.key ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-accent'
                    }`}>
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Période */}
              <select value={logDays} onChange={e => setLogDays(Number(e.target.value))}
                className="text-sm rounded-lg border bg-background px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/30">
                {[1, 7, 14, 30, 60, 90, 365].map(d => (
                  <option key={d} value={d}>Derniers {d} jour{d > 1 ? 's' : ''}</option>
                ))}
              </select>

              {selectedKey && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-sm">
                  <span className="text-primary font-medium">🔑 {selectedKey.email || selectedKey.key_masked}</span>
                  <button onClick={() => setSelectedKey(null)} className="text-muted-foreground hover:text-foreground">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              <span className="text-sm text-muted-foreground ml-auto tabular-nums">
                {nf(displayLogs.length)} affichée(s) sur {nf(displayTotal)}
              </span>
            </div>

            {(loadingAllLogs || loadingLogs) ? (
              <div className="flex justify-center py-12"><Loader className="!size-6" /></div>
            ) : (
              <div className="space-y-2">
                {displayLogs.length === 0 ? (
                  <div className="rounded-xl border bg-card text-center py-16 text-muted-foreground">
                    <p className="text-4xl mb-3">📋</p>
                    <p className="font-medium">Aucun log pour cette période</p>
                    <p className="text-sm mt-1">Les recherches et questions agent apparaîtront ici</p>
                  </div>
                ) : displayLogs.map(log => (
                  <div key={log.id} className="rounded-xl border bg-card p-4 hover:border-primary/20 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">

                        {/* ── Ligne meta ── */}
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <SourceBadge source={log.source} />
                          {log.agent_name && (
                            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">{log.agent_name}</span>
                          )}
                          {log.fiche_context && (
                            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-mono">{log.fiche_context}</span>
                          )}
                          {log.key_name && (
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">🧪 {log.key_name}</span>
                          )}
                          {!log.key_name && log.email && <span className="text-xs text-muted-foreground">{log.email}</span>}
                          {!log.key_name && !log.email && log.uid && (
                            <span className="text-[10px] font-mono text-muted-foreground">uid:{log.uid.slice(0, 10)}…</span>
                          )}
                          {!log.key_name && !log.email && !log.uid && log.ip && (
                            <span className="text-[10px] font-mono text-muted-foreground">{log.ip}</span>
                          )}
                          <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">{fmtDate(log.timestamp)}</span>
                        </div>

                        {/* ── Contenu selon source ── */}
                        {log.source === 'fiche' ? (
                          /* Consultation fiche */
                          <div>
                            <p className="text-sm font-medium text-foreground mb-1">
                              📄 {log.ecli || log.number || log.query || '—'}
                            </p>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mb-1">
                              {log.chamber       && <span>{log.chamber}</span>}
                              {log.decision_date && <span>{log.decision_date}</span>}
                              {log.jurisdiction  && <span className="font-mono">{log.jurisdiction}</span>}
                            </div>
                            {log.referrer && (
                              <p className="text-[10px] text-muted-foreground/70 truncate font-mono">
                                via {log.referrer.replace(/^https?:\/\/[^/]+/, '') || '/'}
                              </p>
                            )}
                          </div>
                        ) : log.source === 'agent' ? (
                          /* SmartSearch agent */
                          <div>
                            <p className="text-sm font-medium text-foreground line-clamp-2 mb-1">
                              🧭 {log.query}
                            </p>
                            {log.fiche_id && (
                              <p className="text-[11px] font-mono text-primary truncate">contexte : {log.fiche_id}</p>
                            )}
                            {log.response_time_ms != null && log.response_time_ms > 0 && (
                              <span className="text-[10px] text-muted-foreground">⏱ {fmtMs(log.response_time_ms)}</span>
                            )}
                          </div>
                        ) : (
                          /* Recherche classique (public / mcp / fiche_agent) */
                          <div>
                            <p className="text-sm font-medium text-foreground line-clamp-2 mb-1.5">
                              {log.source === 'fiche_agent' ? '🤖 ' : '🔍 '}{log.query}
                            </p>
                            {log.fiche_id && (
                              <p className="text-[11px] font-mono text-primary mb-1 truncate">fiche : {log.fiche_id}</p>
                            )}
                            {Object.keys(log.filters || {}).length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-1.5">
                                {Object.entries(log.filters).map(([k, v]) => v && (
                                  <span key={k} className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-mono">
                                    {k}={v}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              {log.source !== 'fiche_agent' && (
                                <>
                                  <span>📄 {log.results_count} résultat{log.results_count !== 1 ? 's' : ''}</span>
                                  <span>⏱ {fmtMs(log.response_time_ms)}</span>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  </div>
                ))}
                {hasMore && (
                  <div className="flex justify-center pt-2">
                    <button onClick={loadMore} disabled={loadingMore}
                      className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border hover:bg-accent disabled:opacity-60">
                      {loadingMore && <Loader className="!size-4" />}
                      Charger 100 de plus
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function APIKeys() {
  return (
    <Page>
      <APIKeysContent />
    </Page>
  );
}